import { useState, useEffect } from 'react';
import { getDashboardStats, getDashboardCharts } from '../api/services/dashboardService';
import LoadingSpinner from './LoadingSpinner';

function Dashboard() {
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalAssessments: 0,
    publishedAssessments: 0,
    completionRate: 0,
  });
  const [charts, setCharts] = useState({
    assessmentStatus: { published: 0, draft: 0 },
    recentAssessments: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsResponse, chartsResponse] = await Promise.all([
          getDashboardStats(),
          getDashboardCharts(),
        ]);

        if (statsResponse.success) {
          setStats(statsResponse.data);
        }

        if (chartsResponse.success) {
          setCharts(chartsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statsDisplay = [
    {
      label: 'Total Students',
      value: stats.totalStudents.toString(),
      icon: '👥',
    },
    {
      label: 'Total Assessments',
      value: stats.totalAssessments.toString(),
      icon: '📄',
    },
    {
      label: 'Published Assessments',
      value: stats.publishedAssessments.toString(),
      icon: '📊',
    },
    {
      label: 'Completion Rate',
      value: `${stats.completionRate}%`,
      icon: '📈',
    },
  ];

  return (
    <div className="p-8" style={{ backgroundColor: 'var(--screen-background-color)' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-text-main mb-2">Dashboard</h1>
        <p className="text-text-sub">
          Welcome back! Here's an overview of your university's assessment portal.
        </p>
      </div>

      {/* Statistics Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" text="Loading dashboard data..." />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statsDisplay.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-gray-600 text-sm mb-2">{stat.label}</p>
                    <p className="text-3xl font-bold text-black">{stat.value}</p>
                  </div>
                  <div className="text-3xl">{stat.icon}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assessment Status Overview */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xl">⏰</span>
            <h2 className="text-xl font-semibold text-black">Assessment Status Overview</h2>
          </div>
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-48 h-48">
              {/* Simple Pie Chart Representation */}
              <svg viewBox="0 0 100 100" className="transform -rotate-90">
                {/* Published 67% */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="20"
                  strokeDasharray={`${2 * Math.PI * 40 * (charts.assessmentStatus.published / 100)} ${2 * Math.PI * 40}`}
                />
                {/* Draft 33% */}
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#374151"
                  strokeWidth="20"
                  strokeDasharray={`${2 * Math.PI * 40 * (charts.assessmentStatus.draft / 100)} ${2 * Math.PI * 40}`}
                  strokeDashoffset={`-${2 * Math.PI * 40 * (charts.assessmentStatus.published / 100)}`}
                />
              </svg>
            </div>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-gray-700"></div>
              <span className="text-sm text-gray-600">Draft: {charts.assessmentStatus.draft}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600">Published: {charts.assessmentStatus.published}%</span>
            </div>
          </div>
        </div>

        {/* Recent Assessments Performance */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xl">📊</span>
            <h2 className="text-xl font-semibold text-black">Recent Assessments Performance</h2>
          </div>
          <div className="h-64 flex items-end justify-center gap-8">
            {/* Bar Chart */}
            {charts.recentAssessments.map((assessment, index) => (
              <div key={assessment.id} className="flex flex-col items-center gap-2">
                <div className="flex gap-1 items-end h-48">
                  <div 
                    className="w-12 bg-blue-500 rounded-t" 
                    style={{ height: `${(assessment.avgScore / 100) * 100}%` }}
                  ></div>
                  <div 
                    className="w-12 bg-green-500 rounded-t" 
                    style={{ height: `${assessment.completionRate}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-600 text-center max-w-[80px] truncate">
                  {assessment.title}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500"></div>
              <span className="text-sm text-gray-600">Avg Score</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span className="text-sm text-gray-600">Completion %</span>
            </div>
          </div>
        </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
